import React, { useContext, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppRulesContext } from "./App";
import DashboardLayout from "./DashboardLayout";
import GuardedRoute from "./components/GuardedRoute";
import ScrollToTop from "./components/ScrollToTop";
import SelfFilingSuccess from "./components/SelfFilingSuccess";
import RegistrationSuccessfull from "./components/auth/RegistrationSuccessfull";
import HintModal from "./components/modals/HintModal";
import { ShowToastMsg } from "./components/placeholders";
import { AppAuthRoles } from "./config/app-constants";
import { ITRFilingNavigator } from "./navigator";
import PlanNavigator from "./navigator/PlanNavigator";
import TaxExpertNavigator from "./navigator/TaxExpertNavigator";
import UserNavigator from "./navigator/UserNavigator";
import PageNotFound from "./pages/PageNotFound";
import AuthContainer from "./pages/auth/AuthContainer";
import SetLoggedInUser from "./pages/auth/SetLoggedInUser";
import GlobalSettings from "./pages/global-settings/GlobalSettings";
import Home from "./pages/home/Home";
import MyProfile from "./pages/logged-in-user/MyProfile";
import AboutUs from "./pages/public-pages/AboutUs";
import ContactUs from "./pages/public-pages/ContactUs";
import PrivacyPolicy from "./pages/public-pages/PrivacyPolicy";
import ProcessPlanPurchase from "./pages/public-pages/ProcessPlanPurchase";
import PublicPricingPlans from "./pages/public-pages/PublicPricingPlans";
import TermsAndCondition from "./pages/public-pages/TermsAndCondition";
import { useAppSelector } from "./redux/Store";
import { loggedInUserSelector } from "./redux/slice/LoggedInUserSlice";
import { UtilService } from "./services";
import BlogAdminRoutes from "./pages/blog-admin";

const AppNavigator: React.FC = () => {
  const { rulesSvc } = useContext(AppRulesContext);
  const { loggedInUser } = useAppSelector(loggedInUserSelector);

  const getDefaultRoute = useMemo(
    () => (loggedInUser?.role === AppAuthRoles.ADMIN ? "/itr-filings" : "/home"),
    [loggedInUser]
  );

  const PrivateRoute = ({ children, redirect }: { children: any; redirect: string }) => {
    const isAuthenticated = UtilService.isAuthenticated();
    const location = useLocation();

    return isAuthenticated ? (
      children
    ) : (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirect || location.pathname)}`}
        replace={true}
      />
    );
  };

  const wrapPrivateRoute = (element: JSX.Element, redirect: string) => {
    return <PrivateRoute redirect={redirect}>{element}</PrivateRoute>;
  };

  const wrapPublicRoute = (element: JSX.Element) => {
    const isAuthenticated = UtilService.isAuthenticated();

    return isAuthenticated ? <Navigate to={getDefaultRoute} replace={true} /> : element;
  };

  return (
    <>
      <BrowserRouter>
        <ScrollToTop>
          <Routes>
            <Route element={<SetLoggedInUser />}>
              <Route path="" element={wrapPublicRoute(<Navigate to="login" />)} />
              <Route path="*" element={wrapPublicRoute(<Navigate to="login" />)} />
              <Route path="/login" element={wrapPublicRoute(<AuthContainer />)} />
              <Route path="/register" element={wrapPublicRoute(<AuthContainer />)} />
              <Route path="/verification" element={wrapPublicRoute(<AuthContainer />)} />
              <Route path="/reset-password" element={wrapPublicRoute(<AuthContainer />)} />
              <Route path="/forgot-password" element={wrapPublicRoute(<AuthContainer />)} />
              {/* for public purchasing a plan*/}
              <Route path="/public-assisted-plans" element={<PublicPricingPlans />} />
              <Route path="/itr-filings/assisted/:planUUID" element={<ProcessPlanPurchase />} />
              <Route path="/terms-conditions" element={<TermsAndCondition />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />

              <Route element={<DashboardLayout />}>
                <Route path="" element={<Navigate to={getDefaultRoute} />} />
                <Route
                  path="/home/*"
                  element={
                    <GuardedRoute
                      rules={rulesSvc.createEFiling}
                      element={wrapPrivateRoute(<Home />, "home")}
                    />
                  }
                />
                <Route
                  path="/itr-filings/*"
                  element={wrapPrivateRoute(<ITRFilingNavigator />, "itr-filings")}
                />
                <Route
                  path="/payment-successful"
                  element={wrapPrivateRoute(<SelfFilingSuccess />, "itr-filings")}
                />
                <Route
                  path="/register-successful"
                  element={wrapPrivateRoute(<RegistrationSuccessfull />, "itr-filings")}
                />

                <Route path="/users/*" element={wrapPrivateRoute(<UserNavigator />, "users")} />
                <Route
                  path="/tax-experts/*"
                  element={wrapPrivateRoute(<TaxExpertNavigator />, "tax-experts")}
                />
                <Route path="/plans/*" element={wrapPrivateRoute(<PlanNavigator />, "plans")} />
                <Route
                  path="/settings/*"
                  element={wrapPrivateRoute(<GlobalSettings />, "settings")}
                />
                <Route
                  path="/blog/*"
                  element={wrapPrivateRoute(<BlogAdminRoutes />, "blog")}
                />

                <Route path="/profile/*" element={wrapPrivateRoute(<MyProfile />, "profile")} />
              </Route>
              <Route path="*" element={<PageNotFound />} />
            </Route>
          </Routes>
        </ScrollToTop>
      </BrowserRouter>

      <ShowToastMsg />
      <HintModal />
    </>
  );
};

export default AppNavigator;
